/**
 * Turn an HTTP failure into a message a person can read.
 *
 * We never show raw status codes, the word "HTTP", exception reprs, or URL
 * paths to users. If the backend returned a clean human sentence as its
 * `detail`, we prefer that; otherwise we fall back to a friendly sentence
 * chosen by status. Keep the technical bits (status/path) for logging via
 * the error object's fields — not in the user-facing string.
 */

const BY_STATUS: Record<number, string> = {
  400: "That request didn't look right. Please check your input and try again.",
  401: "Your session expired. Please sign in again.",
  403: "You don't have permission to do that.",
  404: "We couldn't find what you were looking for.",
  408: "That took too long. Please try again.",
  409: "That conflicts with the current state. Refresh and try again.",
  413: "That's too large to upload.",
  422: "We couldn't process that request. Please check your input and try again.",
  429: "Too many requests — please slow down and try again in a moment.",
  500: "Something went wrong on our end. Please try again.",
  502: "We're having trouble reaching the service. Please try again.",
  503: "The service is temporarily unavailable. Please try again shortly.",
  504: "That took too long to respond. Please try again.",
};

const GENERIC = "Something went wrong. Please try again.";

/**
 * A backend `detail` is worth showing only if it reads like a human sentence.
 * Reject code-shaped strings: exception reprs (`Foo(...)`, `KeyError: ...`),
 * bare snake_case/enum tokens, JSON/dicts, stack traces, or anything with no
 * spaces (a single token is almost always a code, not a message).
 */
function isHumanDetail(detail: string): boolean {
  const d = detail.trim();
  if (!d) return false;
  if (!d.includes(" ")) return false; // single token → a code/enum
  if (/[{}[\]]/.test(d)) return false; // JSON / dict / list repr
  if (/\b\w+Error\b/.test(d)) return false; // KeyError, ValidationError, …
  if (/\b\w+Exception\b/.test(d)) return false;
  if (/\bTraceback\b/i.test(d)) return false;
  if (/^\s*at\s/m.test(d)) return false; // stack frame
  return true;
}

/**
 * Fine-grained messages keyed on a structured backend hint (`fallback`/`code`
 * in the error payload), independent of the coarse HTTP status. These beat the
 * generic BY_STATUS text — e.g. a connector-OAuth 409 with
 * `fallback: "manual_oauth"` is NOT a stale-state conflict, so "refresh and try
 * again" is actively misleading.
 */
const BY_HINT: Record<string, string> = {
  manual_oauth:
    "This server doesn't support automatic sign-in. Choose a different auth mode — paste a token (Shared credential / Custom headers) or configure OAuth manually.",
  subscription_inactive:
    "Your subscription is inactive. Reactivate it to continue.",
};

/** Pull a structured hint (`fallback` or `code`) out of a backend payload,
 *  whether it's at the top level or under `detail`. */
function hintFromPayload(payload: unknown): string | undefined {
  if (!payload || typeof payload !== "object") return undefined;
  const p = payload as Record<string, unknown>;
  const d = (p.detail && typeof p.detail === "object" ? p.detail : p) as Record<
    string,
    unknown
  >;
  const h = d.fallback ?? d.code;
  return typeof h === "string" ? h : undefined;
}

/**
 * @param status HTTP status code
 * @param detail optional backend-provided detail string
 * @param payload optional raw backend body (object/string) — used for
 *   fine-grained, hint-based messages before falling back to status text
 * @returns a user-facing sentence — never contains codes, "HTTP", or paths
 */
export function friendlyError(
  status: number,
  detail?: string | null,
  payload?: unknown,
): string {
  // 1. A structured hint gives the most specific, correct message.
  const hint = hintFromPayload(payload);
  if (hint && BY_HINT[hint]) return BY_HINT[hint];
  // 2. A clean human sentence from the backend.
  if (detail && isHumanDetail(detail)) return detail.trim();
  // 3. Coarse fallback by status.
  return BY_STATUS[status] ?? GENERIC;
}
